import { useQuery } from '@tanstack/react-query';
import { legKey, legsOf, type LegMode, type MapPoint, type RoadLeg } from '@travessa/shared';
import { api } from '../api';
import { PIN_ACCENT } from './pins';

/**
 * Roads for the days on the map. The legs are cut here (the same `legsOf` the
 * API uses), asked for in batches the API accepts, and keyed by `legKey` — so
 * a filter that hides a day re-uses what is already loaded.
 */

const BATCH = 120;

export interface DayStops {
  dayIndex: number;
  color: string;
  stops: MapPoint[];
}

export interface DayLeg {
  dayIndex: number;
  color: string;
  key: string;
  mode: LegMode;
  from: MapPoint;
  to: MapPoint;
}

export function dayLegs(days: DayStops[]): DayLeg[] {
  return days.flatMap((day) =>
    legsOf(day.stops).map((leg) => ({
      dayIndex: day.dayIndex,
      color: day.color,
      key: legKey(leg.mode, leg.from, leg.to),
      mode: leg.mode,
      from: leg.from,
      to: leg.to,
    })),
  );
}

/** Each day's located stops, in the day's order — what the map's lines join. */
export function tripDayStops(
  days: Array<{ dayIndex: number; color: string | null; placeIds: string[] }>,
  places: Array<{ id: string; lat: number | null; lng: number | null }>,
): DayStops[] {
  const byId = new Map(places.map((place) => [place.id, place]));
  return days.map((day) => ({
    dayIndex: day.dayIndex,
    color: day.color ?? PIN_ACCENT,
    stops: day.placeIds
      .map((id) => byId.get(id))
      .filter((p): p is { id: string; lat: number; lng: number } => p?.lat != null && p.lng != null)
      .map((p) => ({ lat: p.lat, lng: p.lng })),
  }));
}

/**
 * The query for a set of legs. Shared by the map and by the offline prefetch
 * (`lib/offline/persist.ts`), so a trip opened once has its roads on the
 * device under the very key the map will ask for.
 */
export function roadsQuery(legs: DayLeg[]) {
  const wanted = [...new Map(legs.map((leg) => [leg.key, leg])).values()];
  return {
    wanted,
    queryKey: ['maps', 'roads', wanted.map((leg) => leg.key).sort().join('|')],
    queryFn: async () => {
      const out = new Map<string, RoadLeg>();
      for (let i = 0; i < wanted.length; i += BATCH) {
        const batch = wanted.slice(i, i + BATCH);
        const result = await api.post<{ legs: RoadLeg[] }>('/maps/roads', {
          legs: batch.map((leg) => ({ from: leg.from, to: leg.to })),
        });
        for (const leg of result.legs) out.set(leg.key, leg);
      }
      return out;
    },
  };
}

export function useRoads(legs: DayLeg[], enabled: boolean) {
  const { wanted, queryKey, queryFn } = roadsQuery(legs);
  return useQuery({
    queryKey,
    queryFn,
    enabled: enabled && wanted.length > 0,
    staleTime: Infinity,
    // A leg the routers could not answer comes back straight and `pending`:
    // ask again a little later (answered legs are cached server-side, so a
    // repeat costs only the missing ones), a few times at most.
    refetchInterval: (query) =>
      query.state.dataUpdateCount < 6 && [...(query.state.data?.values() ?? [])].some((leg) => leg.pending) ? 20_000 : false,
    gcTime: 30 * 60_000,
    retry: 1,
  });
}

/** «12 км · 40 мин»: distance and time on the move for a set of legs. */
export function travelSummary(legs: RoadLeg[]): string | null {
  if (!legs.length) return null;
  const meters = legs.reduce((sum, leg) => sum + leg.distanceM, 0);
  const seconds = legs.reduce((sum, leg) => sum + leg.durationS, 0);
  const distance = meters < 1000 ? `${Math.round(meters / 10) * 10} м` : `${(meters / 1000).toFixed(meters < 20_000 ? 1 : 0).replace('.', ',')} км`;
  const minutes = Math.round(seconds / 60);
  const time = minutes < 60 ? `${minutes} мин` : `${Math.floor(minutes / 60)} ч ${minutes % 60 ? `${minutes % 60} мин` : ''}`.trim();
  const approx = legs.some((leg) => leg.straight) ? '≈ ' : '';
  return `${approx}${distance} · ${time}`;
}
